import { useEffect } from "react";

/** Default browser tab title (must match index.html). */
export const DEFAULT_DOCUMENT_TITLE = "Robotic Imaging Inc";

/** Standard suffix for client/asset management pages. */
export const DOCUMENT_TITLE_SUFFIX = "| Asset Management";

/**
 * Builds a consistent page title from segments and an optional suffix.
 * Use this so all views share the same format: "Segment A - Segment B | Suffix".
 *
 * @param parts - Title segments (e.g. [clientName, sectionLabel])
 * @param suffix - Optional suffix (default: DOCUMENT_TITLE_SUFFIX). Use for context like "| Asset Detail" or "| Property Detail".
 */
export function buildPageTitle(
  parts: string[],
  suffix: string = DOCUMENT_TITLE_SUFFIX
): string {
  if (parts.length === 0) return DEFAULT_DOCUMENT_TITLE;
  const main = parts.filter(Boolean).join(" - ");
  return main ? `${main} ${suffix}` : DEFAULT_DOCUMENT_TITLE;
}

/**
 * Centralized hook to set the browser tab title (document.title).
 * Use in every view so the tab title is always in sync; pass null to restore the default.
 *
 * @param title - Full title string, or null to use DEFAULT_DOCUMENT_TITLE
 */
export function useDocumentTitle(title: string | null): void {
  useEffect(() => {
    document.title = title ?? DEFAULT_DOCUMENT_TITLE;
  }, [title]);
}
